import * as Yup from 'yup';
import {
	parse,
	isValid,
	isAfter,
	isBefore,
	addDays,
	addMonths,
	differenceInMonths,
	isSameDay,
} from 'date-fns';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';

const useValidationSchema = () => {
	const {
		minPolicyPeriod,
		maxPolicyPeriod,
		maxForwardDays,
		minDate,
		maxDate,
		backdatingDays,
		minBackdatedDate,
	} = useQuoteContext();
	const dateSchema = {
		startDate: Yup.date()
			.typeError(({ type }) => `Policy start date is required`)
			.required(`Policy start date is required`)
			.test(
				'is-future-date',
				'Please update the policy period to the current date or a future date.',
				(value) => {
					if (!value) return false;
					return isValid(value) && (isAfter(value, minDate) || isSameDay(value, minDate));
				}
			)
			.test(
				'is-valid-start-date',
				`Policy period cannot be forward dated more than ${maxForwardDays} days.`,
				(value) => {
					if (!value) return false;
					const isFutureDate = isAfter(value, minDate) || isSameDay(value, minDate);
					const isWithinPeriod = isBefore(value, maxDate) || isSameDay(value, maxDate);
					return isValid(value) && isFutureDate && isWithinPeriod;
				}
			),
		endDate: Yup.date()
			.typeError(({ type }) => `Policy end date is required`)
			.required(`Policy end date is required`)
			.test(
				'is-valid-period',
				'Policy period end date cannot be before or equal to the start date.',
				function (value) {
					const { startDate } = this.parent;

					if (!value || !startDate) return false;
					return isValid(value) && isValid(startDate) && isAfter(value, startDate);
				}
			)
			.test(
				'is-at-least-9-months',
				`Policy period shorter than ${minPolicyPeriod} months. Refer to Delta underwriter.`,
				function (value) {
					const { startDate } = this.parent;
					if (!value || !startDate) return false;

					const minimumDate = addMonths(startDate ?? minDate, minPolicyPeriod);
					return (
						isValid(value) &&
						isValid(startDate) &&
						(isAfter(value, minimumDate) || isSameDay(value, minimumDate))
					);
				}
			)
			.test(
				'is-no-more-than-16-months',
				`Policy period longer than ${maxPolicyPeriod} months. Reduce policy period to continue.`,
				function (value) {
					const { startDate } = this.parent;
					if (!value || !startDate) return false;

					const maximumDate = addMonths(startDate ?? minDate, maxPolicyPeriod);
					return (
						isValid(value) &&
						isValid(startDate) &&
						(isBefore(value, maximumDate) || isSameDay(value, maximumDate))
					);
				}
			),
	};

	const validationSchema = [
		Yup.object().shape({
			companyName: Yup.string().required(`Company name is required.`),
			// tradingName: Yup.boolean(),
			occupation: Yup.string().required(`Occupation name is required.`),
			// numberOfEmployees: Yup.string().required(`Select the number of employees`),
			// turnOver: Yup.string().required(`Select the turnover`),
			// estimatedTurnOver: Yup.string().required(`Select the estimated turnover`),
			streetName: Yup.string().required('Street name is required'),
			city: Yup.string().required('City is required'),
			region: Yup.string().required('Region is required'),
			postcode: Yup.string()
				.required()
				.min(4, 'Postcode must be 4 digits')
				.max(4, 'Postcode must be 4 digits'),

			...dateSchema,
			totals: Yup.array()
				.of(Yup.number())
				.test('totals-test', {}, (value, context) => {
					const [annualTurnover, estimatedTurnover, staffNumbers] = value ?? [0, 0, 0];
					const { path, createError } = context;

					if ((annualTurnover ?? 0) <= 0) {
						return createError({
							path: `${path}[0]`,
							message: 'Total annual turnover must be greater than 0',
						});
					}
					if ((estimatedTurnover ?? 0) <= 0) {
						return createError({
							path: `${path}[1]`,
							message: 'Total estimated turnover must be greater than 0',
						});
					}
					if ((staffNumbers ?? 0) <= 0) {
						return createError({
							path: `${path}[2]`,
							message: 'Total staff numbers must be greater than 0',
						});
					}
					return true;
				}),
		}),
		Yup.object().shape(
			{
				products: Yup.array().when('packages', {
					is: (packages: any) => {
						return !packages || (packages ?? []).length === 0;
					},
					then: () => Yup.array().min(1, 'Pick at least 1 item').required(),
					otherwise: () => Yup.array(),
				}),
				packages: Yup.array().when('products', {
					is: (products: any) => {
						return !products || (products ?? []).length === 0;
					},
					then: () => Yup.array().min(1, 'Pick at least 1 item').required(),
					otherwise: () => Yup.array(),
				}),
				...dateSchema,
			},
			[['products', 'packages']]
		),
		Yup.object().shape({ ...dateSchema }),
		Yup.object().shape({
			// streetName: Yup.string().required("Street name is required"),
			// city: Yup.string().required("City is required"),
			// region: Yup.number().required("Region is required"),
			// postcode: Yup.string()
			// 	.required()
			// 	.min(4, "Postcode must be 4 digits")
			// 	.max(4, "Postcode must be 4 digits"),
			...dateSchema,
		}),
	];

	const backdatedDateSchema = Yup.object().shape({
		startDate: Yup.date()
			.typeError(({ type }) => `Policy start date is required`)
			.required(`Policy start date is required`)
			.test(
				'is-future-date',
				backdatingDays > 0
					? `Policy period cannot be backdated more than ${backdatingDays} days`
					: 'Please update the policy period to the current date or a future date.',
				(value) => {
					if (!value) return false;
                    console.log('MIN BACKDATED DATE', minBackdatedDate, 'BACKDATING DAYS', backdatingDays);
					return (
						isValid(value) &&
						(isAfter(value, minBackdatedDate) || isSameDay(value, minBackdatedDate))
					);
				}
			)
			.test(
				'is-valid-start-date',
				`Policy period cannot be forward dated more than ${maxForwardDays} days.`,
				(value) => {
					if (!value) return false;
					const isFutureDate = isAfter(value, minBackdatedDate) || isSameDay(value, minBackdatedDate);
					const isWithinPeriod = isBefore(value, maxDate) || isSameDay(value, maxDate);
					return isValid(value) && isFutureDate && isWithinPeriod;
				}
			),
	});

	return { validationSchema, backdatedDateSchema };
};

export default useValidationSchema;
